/* Favorites drawer — slides in from the right, shows wishlisted products with remove and add-to-cart actions. */

import { useFavorites, FavoriteItem } from '@/contexts/FavoritesContext';
import { useCart } from '@/contexts/CartContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Heart, ShoppingCart, X, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

interface FavoritesDrawerProps {
  open: boolean;
  onClose: () => void;
}

export default function FavoritesDrawer({ open, onClose }: FavoritesDrawerProps) {
  const { favorites, removeFavorite, clearFavorites } = useFavorites();
  const { addItem } = useCart();

  const formatPrice = (price?: number) => {
    if (!price) return '';
    return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(price);
  };

  const handleAddToCart = async (item: FavoriteItem) => {
    try {
      await addItem({
        product_variant_id: item.product_variant_id,
        product_id: item.product_id,
        product_name: item.product_name,
        variant_size: item.variant_size,
        unit_price: item.unit_price || 0,
        image_url: item.image_url,
        slug: item.slug,
        quantity: 1,
      });
      toast.success(`${item.product_name} toegevoegd aan winkelwagen`);
    } catch {
      toast.error('Er ging iets mis bij het toevoegen aan de winkelwagen');
    }
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg flex flex-col">
        <SheetHeader className="border-b pb-1">
          <div className="flex items-center justify-between">
            <SheetTitle className="flex items-center gap-2 text-2xl font-bold text-jv-primary">
              <Heart className="h-6 w-6 text-jv-accent" fill="currentColor" />
              Favorieten
            </SheetTitle>
            {favorites.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="text-gray-500 hover:text-red-600"
                onClick={clearFavorites}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Alles wissen
              </Button>
            )}
          </div>
          <p className="text-sm text-gray-600 text-left">
            {favorites.length === 0 ? 'Nog geen favorieten' : `${favorites.length} ${favorites.length === 1 ? 'product' : 'producten'}`}
          </p>
        </SheetHeader>

        {favorites.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center py-12">
            <Heart className="h-24 w-24 text-gray-200 mb-4" />
            <p className="text-lg font-medium text-gray-600 mb-2">Nog geen favorieten</p>
            <p className="text-sm text-gray-500 mb-6">Bewaar producten die je leuk vindt door op het hartje te klikken</p>
            <Button asChild onClick={onClose}>
              <Link to="/haagplanten">Bekijk haagplanten</Link>
            </Button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto">
              <div className="space-y-2">
                {favorites.map((item, index) => (
                  <div key={item.id} className={`relative flex gap-3 p-3 sm:p-4 rounded-lg ${index % 2 === 1 ? 'bg-gray-50' : ''}`}>
                    {item.image_url && (
                      <div
                        className="hidden sm:block w-24 h-42 flex-shrink-0 bg-white overflow-hidden"
                        style={{ borderTopLeftRadius: '20px', borderBottomRightRadius: '20px' }}
                      >
                        <img src={item.image_url} alt={item.product_name} className="h-full object-cover max-h-full max-w-full" />
                      </div>
                    )}

                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start gap-3 mb-2">
                        <div className="flex-1">
                          <Link to={item.slug ? `/${item.slug}` : '#'} onClick={onClose}>
                            <h3 className="font-semibold text-jv-primary mb-1 text-sm sm:text-base hover:underline cursor-pointer">
                              {item.product_name}{item.variant_size ? ` ${item.variant_size}` : ''}
                            </h3>
                          </Link>
                          {item.unit_price ? (
                            <p className="text-sm font-bold text-jv-primary">{formatPrice(item.unit_price)}</p>
                          ) : null}
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-gray-400 hover:text-red-600 hover:bg-red-50 flex-shrink-0"
                          onClick={() => removeFavorite(item.id)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>

                      <Button
                        size="sm"
                        className="bg-jv-accent hover:bg-jv-accent/90 text-white"
                        onClick={() => handleAddToCart(item)}
                      >
                        <ShoppingCart className="h-4 w-4 mr-1" />
                        In winkelwagen
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t pt-4">
              <Button
                onClick={onClose}
                className="w-full bg-jv-secondary text-jv-primary border-2 border-jv-secondary hover:bg-jv-secondary/80"
                style={{ borderBottomLeftRadius: '20px', borderTopRightRadius: '20px' }}
                asChild
              >
                <Link to="/haagplanten">Verder winkelen</Link>
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
